import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, Search, ShoppingBag, Copy } from "lucide-react";
import { toast } from "sonner";
import { API, allProducts, loadCatalog } from "../../lib/store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";

export function SeoTab({ auth }) {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [search, setSearch] = useState("");
  const [rerunning, setRerunning] = useState(null);
  const [tick, setTick] = useState(0);
  const [feed, setFeed] = useState(null);

  const token = auth.headers.Authorization;
  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/seo/status`, { headers: { Authorization: token } });
      setStatus(r.data);
      return r.data;
    } catch (e) {
      console.warn("SEO status fetch failed:", e?.message);
      return null;
    }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    axios.get(`${API}/admin/merchant-feed/health`, { headers: { Authorization: token } })
      .then((r) => setFeed(r.data))
      .catch((e) => console.warn("Feed health fetch failed:", e?.message));
  }, [token]);

  useEffect(() => {
    if (!status?.running) return;
    const id = setInterval(async () => {
      const s = await fetchStatus();
      if (s && !s.running) {
        await loadCatalog();
        setTick((t) => t + 1);
        toast.success(`SEO enrichment complete — ${s.done} products enriched!`);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [status?.running, fetchStatus]);

  const startRun = async (force) => {
    const pending = force ? status?.productCount : status?.pendingCount;
    const ok = window.confirm(
      `Run AI SEO enrichment for ${pending ?? "?"} product${pending === 1 ? "" : "s"}?\n\n` +
        "This uses a small one-time amount of your Universal Key balance. " +
        "Already-enriched products are cached and skipped on future runs, so the 30-minute auto-sync stays free."
    );
    if (!ok) return;
    setStarting(true);
    try {
      await axios.post(`${API}/admin/seo/enrich`, { force }, auth);
      toast.info("AI enrichment started — this runs in the background (15-30 min for the full catalog)");
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start enrichment");
    } finally {
      setStarting(false);
    }
  };

  const rerunOne = async (handle) => {
    setRerunning(handle);
    try {
      await axios.post(`${API}/admin/seo/enrich-one`, { handle }, auth);
      await loadCatalog();
      setTick((t) => t + 1);
      toast.success("Product re-enriched");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-enrichment failed");
    } finally {
      setRerunning(null);
    }
  };

  const results = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = q
      ? allProducts.filter((p) => p.title.toLowerCase().includes(q) || p.handle.includes(q))
      : allProducts;
    return list.slice(0, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tick]);

  const pct = status?.running && status.total > 0 ? Math.round(((status.done + status.failed) / status.total) * 100) : 0;

  return (
    <div className="space-y-8">
      {/* Status / run card */}
      <div className="bg-white border border-[#eee6d6] p-6" data-testid="seo-status-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
              <Sparkles size={18} className="text-[#a8842c]" /> AI SEO Enrichment
            </h3>
            <p className="text-sm text-[#8f8571] mt-1 max-w-xl">
              AI writes a search-optimized title, meta description and keywords for every product. Results are cached —
              each product is only processed once (or again if it changes in Shopify), and new products are enriched
              automatically after each sync.
            </p>
            {status && (
              <p className="text-xs text-[#a8842c] mt-2" data-testid="seo-status-counts">
                {status.enrichedCount} of {status.productCount} products enriched
                {status.pendingCount > 0 && !status.running ? ` · ${status.pendingCount} pending` : ""}
                {status.lastError ? ` · last error: ${status.lastError.slice(0, 80)}` : ""}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              onClick={() => startRun(false)}
              disabled={starting || status?.running || status?.pendingCount === 0}
              className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-6 transition-colors duration-300"
              data-testid="seo-run-btn"
            >
              <Sparkles size={15} className={`mr-2 ${status?.running ? "animate-pulse" : ""}`} />
              {status?.running ? "Enriching..." : status?.pendingCount === 0 ? "All Enriched" : `Run AI Enrichment (${status?.pendingCount ?? "…"})`}
            </Button>
            {!status?.running && status?.enrichedCount > 0 && (
              <button onClick={() => startRun(true)} className="text-[11px] uppercase tracking-wider text-[#8f8571] hover:text-[#a8842c] transition-colors" data-testid="seo-rerun-all-btn">
                Re-run all from scratch
              </button>
            )}
          </div>
        </div>
        {status?.running && (
          <div className="mt-5" data-testid="seo-progress">
            <div className="flex justify-between text-xs text-[#8f8571] mb-1.5">
              <span>{status.done + status.failed} / {status.total} processed{status.failed > 0 ? ` (${status.failed} failed)` : ""}</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 bg-[#f4eddc]">
              <div className="h-full bg-[#a8842c] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Google Shopping feed */}
      <div className="bg-white border border-[#eee6d6] p-6" data-testid="merchant-feed-card">
        <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
          <ShoppingBag size={18} className="text-[#a8842c]" /> Google Shopping Feed
        </h3>
        <p className="text-sm text-[#8f8571] mt-1 max-w-xl">
          A live Merchant Center product feed generated straight from your catalog — prices always match the site
          (updates with every 15-minute sync). Add the URL below in Google Merchant Center as a scheduled fetch feed.
        </p>
        {feed && (
          <>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <code className="text-xs bg-[#faf7f0] border border-[#eee6d6] px-3 py-2 select-all" data-testid="merchant-feed-url">{feed.feedUrl}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(feed.feedUrl); toast.success("Feed URL copied"); }}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#a8842c] hover:text-[#171310] transition-colors"
                data-testid="merchant-feed-copy-btn"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            <p className="text-xs text-[#3f6b3a] mt-3" data-testid="merchant-feed-stats">
              ✓ {feed.eligible} of {feed.total} products eligible
              {feed.excluded > 0 && (
                <span className="text-[#8c2f1b]"> · {feed.excluded} excluded ({Object.entries(feed.reasons).map(([k, v]) => `${v} ${k}`).join(", ")})</span>
              )}
            </p>
          </>
        )}
      </div>

      {/* Per-product preview */}
      <div className="bg-white border border-[#eee6d6] p-6">
        <h3 className="font-heading text-xl text-[#171310]">Preview & Re-run per Product</h3>
        <div className="relative mt-4 mb-5">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9482]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products to preview their SEO text..." className="rounded-none border-[#d8cdb5] pl-9 h-11" data-testid="seo-search-input" />
        </div>
        <div className="space-y-4 max-h-[560px] overflow-y-auto pr-1" data-testid="seo-preview-list">
          {results.map((p) => (
            <div key={p.handle} className="border border-[#eee6d6] p-4 flex gap-4" data-testid="seo-preview-item">
              <img src={p.images[0]} alt={p.title} loading="lazy" className="w-16 h-16 object-contain bg-[#faf7f0] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-[#9c9482] truncate">Original: {p.title}</p>
                {p.seoTitle ? (
                  <>
                    <p className="text-sm font-medium text-[#171310] mt-1">{p.seoTitle}</p>
                    <p className="text-xs text-[#5c5240] mt-1 leading-relaxed">{p.seoDescription}</p>
                    {p.keywords?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {p.keywords.map((k) => (
                          <Badge key={k} variant="outline" className="rounded-none border-[#d8cdb5] text-[#7a7160] font-normal text-[10px]">{k}</Badge>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-[#8c2f1b] mt-1">Not enriched yet</p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => rerunOne(p.handle)}
                disabled={rerunning === p.handle || status?.running}
                className="rounded-none border-[#d8cdb5] h-8 text-[11px] uppercase tracking-wider shrink-0 self-start"
                data-testid="seo-rerun-btn"
              >
                <RefreshCw size={12} className={`mr-1.5 ${rerunning === p.handle ? "animate-spin" : ""}`} />
                {p.seoTitle ? "Re-run" : "Enrich"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
